// File: webelieve-frontend/src/features/intake/components/AIAnalysisPanel.tsx
// M7.30: Refactored from generic AI metrics to Intake operational intelligence —
// stage bottleneck analysis, pipeline distribution, and risk summary grounded
// in the supervisory truth layer.

import React from 'react';
import {
  ChartBarIcon,
  ArrowTrendingUpIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import type { DashboardMetrics } from '../api/edgeflowApi';

interface AIAnalysisPanelProps {
  dashboardMetrics: DashboardMetrics;
  onRefresh: () => void;
}

const STAGE_LABELS: Record<string, string> = {
  new_lead: 'New Lead',
  lead: 'Lead',
  contacted: 'Contacted',
  qualifying: 'Qualifying',
  qualified: 'Qualified',
  converted: 'Converted',
  declined: 'Declined',
};

const STAGE_COLORS: Record<string, string> = {
  new_lead: 'bg-blue-500',
  lead: 'bg-blue-400',
  contacted: 'bg-indigo-500',
  qualifying: 'bg-purple-500',
  qualified: 'bg-green-500',
  converted: 'bg-emerald-600',
  declined: 'bg-gray-400',
};

const AIAnalysisPanel: React.FC<AIAnalysisPanelProps> = ({ dashboardMetrics, onRefresh }) => {
  const { supervisory, summary, byStage } = dashboardMetrics;

  // Stage distribution for visualization
  const stages = Object.entries(byStage)
    .filter(([, count]) => count > 0)
    .sort(([, a], [, b]) => b - a);

  const totalInPipeline = stages.reduce((sum, [, count]) => sum + count, 0);

  // Risk items
  const riskItems = [
    { label: 'Missing conflict checks', count: supervisory.missing_conflict_check_count, severity: 'elevated' },
    { label: 'Missing documents', count: supervisory.missing_documents_count, severity: 'attention' },
    { label: 'Unsigned retainers', count: supervisory.retainer_sent_unsigned_count, severity: 'attention' },
    { label: 'Workflow failures', count: supervisory.unresolved_workflow_failures_count, severity: 'urgent' },
    { label: 'High-urgency unreviewed', count: supervisory.high_urgency_unreviewed_count, severity: 'urgent' },
  ].filter(item => item.count > 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
          Pipeline Intelligence
        </h2>
      </div>

      {/* Conversion summary */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-center gap-2 mb-3">
          <ArrowTrendingUpIcon className="w-5 h-5 text-lex-accent" />
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Conversion</h3>
        </div>
        <div className="grid grid-cols-3 gap-3 text-center">
          <div>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">{summary.totalLeads}</p>
            <p className="text-xs text-gray-500">Total Leads</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-lex-success">{summary.totalConverted}</p>
            <p className="text-xs text-gray-500">Converted</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-lex-accent">{summary.conversionRate}%</p>
            <p className="text-xs text-gray-500">Rate</p>
          </div>
        </div>
      </div>

      {/* Stage distribution */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-center gap-2 mb-3">
          <ChartBarIcon className="w-5 h-5 text-lex-accent" />
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
            Stage Distribution
            {supervisory.stage_bottleneck && (
              <span className="ml-2 text-xs font-normal text-lex-warning">
                Bottleneck: {STAGE_LABELS[supervisory.stage_bottleneck] || supervisory.stage_bottleneck}
              </span>
            )}
          </h3>
        </div>
        <div className="space-y-2">
          {stages.map(([stage, count]) => {
            const pct = totalInPipeline > 0 ? (count / totalInPipeline) * 100 : 0;
            const isBottleneck = stage === supervisory.stage_bottleneck;

            return (
              <div key={stage} className="flex items-center gap-2">
                <span className="text-xs font-medium text-gray-700 dark:text-gray-300 w-20 truncate">
                  {STAGE_LABELS[stage] || stage}
                </span>
                <div className="flex-1 h-5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${STAGE_COLORS[stage] || 'bg-gray-500'} ${isBottleneck ? 'ring-2 ring-lex-warning' : ''}`}
                    style={{ width: `${Math.max(pct, 2)}%` }}
                  />
                </div>
                <span className={`text-xs font-bold w-8 text-right ${isBottleneck ? 'text-lex-warning' : 'text-gray-900 dark:text-white'}`}>
                  {count}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Risk summary */}
      {riskItems.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center gap-2 mb-3">
            <ExclamationTriangleIcon className="w-5 h-5 text-lex-warning" />
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Risk Summary</h3>
          </div>
          <div className="space-y-2">
            {riskItems.map((item, idx) => (
              <div key={idx} className="flex items-center justify-between">
                <span className="text-sm text-gray-700 dark:text-gray-300">{item.label}</span>
                <span className={`text-sm font-bold ${
                  item.severity === 'urgent' ? 'text-red-600 dark:text-red-400' :
                  item.severity === 'elevated' ? 'text-orange-600 dark:text-orange-400' :
                  'text-yellow-600 dark:text-yellow-400'
                }`}>
                  {item.count}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {riskItems.length === 0 && (
        <div className="bg-lex-success-bg border border-lex-success/20 rounded-lg p-4 text-center">
          <p className="text-sm font-medium text-lex-success">No operational risks detected</p>
        </div>
      )}
    </div>
  );
};

export default AIAnalysisPanel;
